import { useMemo } from 'react';
import { getSendWindowErrors } from '../utils/sendWindow';
const getTargets = (step) => {
    switch (step.type) {
        case 'check_segment':
            return [step.on_true, step.on_false];
        case 'activity':
            return [step.next];
        case 'ab_split':
            return [step.arms.A, step.arms.B];
    }
};
const hasExitRef = (step) => {
    switch (step.type) {
        case 'check_segment':
            return step.on_true === 'exit' || step.on_false === 'exit';
        case 'activity':
            return step.next === 'exit';
        case 'ab_split':
            return step.arms.A === 'exit' || step.arms.B === 'exit';
    }
};
const DURATION_RE = /^P(?:\d+D)?(?:T(?:\d+H)?(?:\d+M)?)?$/;
const DANGLING_T_RE = /T$/;
const ZERO_DURATIONS = new Set(['P0D', 'PT0H', 'PT0M', 'P0DT0H0M', 'P0DT0H', 'P0DT0M', 'PT0H0M']);
const isValidDuration = (v) => {
    if (!DURATION_RE.test(v))
        return false;
    if (v === 'P' || v === 'PT')
        return false;
    if (DANGLING_T_RE.test(v))
        return false;
    if (ZERO_DURATIONS.has(v))
        return false;
    const parts = v.match(/(\d+)/g);
    return parts != null && parts.some((p) => Number(p) > 0);
};
const useDripGraphValidation = (steps, entryStepId, sendWindowStart, sendWindowEnd) => {
    return useMemo(() => {
        const errors = [];
        const byStepId = new Map();
        const addError = (msg, stepId, source) => {
            errors.push({ message: msg, stepId, source });
            if (stepId) {
                const existing = byStepId.get(stepId) ?? [];
                existing.push(msg);
                byStepId.set(stepId, existing);
            }
        };
        if (steps.length === 0) {
            addError('At least one step is required.');
            addError('At least one activity step is required.');
            return { errors, byStepId };
        }
        const stepMap = new Map(steps.map((s) => [s.id, s]));
        const stepIds = new Set(steps.map((s) => s.id));
        // Rule 1: entry_step_id must resolve
        if (entryStepId == null || !stepIds.has(entryStepId)) {
            addError('Entry step is missing or does not exist.');
        }
        // Rule 6: at least one activity step
        if (!steps.some((s) => s.type === 'activity')) {
            addError('At least one activity step is required.');
        }
        for (const step of steps) {
            // Rule 2: no dangling refs
            const targets = getTargets(step);
            for (const t of targets) {
                if (t !== 'exit' && !stepIds.has(t)) {
                    addError(`Step "${step.id}" references unknown step "${t}".`, step.id);
                }
            }
            // Rule 5: exit_reason required when exit ref exists
            if (hasExitRef(step) && !step.exit_reason) {
                addError(`Step "${step.id}" has an exit but no exit reason.`, step.id);
            }
            // Rule 7: check_segment must have segment_id OR wait_for
            if (step.type === 'check_segment' && step.segment_id == null && step.wait_for == null) {
                addError(`Step "${step.id}" (check_segment) needs a segment or a wait duration.`, step.id);
            }
            // Rule 8: wait_for shape
            if (step.type === 'check_segment' && step.wait_for != null) {
                if (!isValidDuration(step.wait_for)) {
                    addError(`Step "${step.id}" has an invalid wait duration "${step.wait_for}".`, step.id);
                }
            }
        }
        // Rule 9: send window — both values or neither; containment for single-channel, overlap for mixed
        const hasEmail = steps.some((s) => s.type === 'activity' && s.activity === 'send_email');
        const hasSms = steps.some((s) => s.type === 'activity' && s.activity === 'send_sms');
        for (const msg of getSendWindowErrors(hasEmail, hasSms, sendWindowStart, sendWindowEnd)) {
            addError(msg, undefined, 'send_window');
        }
        // Rule 3: every step reachable from entry
        if (entryStepId != null && stepIds.has(entryStepId)) {
            const visited = new Set();
            const queue = [entryStepId];
            visited.add(entryStepId);
            while (queue.length > 0) {
                const curr = queue.shift();
                const s = stepMap.get(curr);
                if (!s)
                    continue;
                for (const t of getTargets(s)) {
                    if (t !== 'exit' && stepIds.has(t) && !visited.has(t)) {
                        visited.add(t);
                        queue.push(t);
                    }
                }
            }
            for (const id of stepIds) {
                if (!visited.has(id)) {
                    addError(`Step "${id}" is unreachable from the entry step.`, id);
                }
            }
        }
        // Rule 4: cycles only through timed check_segment
        if (entryStepId != null && stepIds.has(entryStepId)) {
            const WHITE = 0, GRAY = 1, BLACK = 2;
            const color = new Map();
            for (const id of stepIds)
                color.set(id, WHITE);
            const dfs = (id, path) => {
                color.set(id, GRAY);
                path.push(id);
                const s = stepMap.get(id);
                if (s) {
                    for (const t of getTargets(s)) {
                        if (t === 'exit' || !stepIds.has(t))
                            continue;
                        if (color.get(t) === GRAY) {
                            const cycleStart = path.indexOf(t);
                            const cycle = path.slice(cycleStart);
                            const hasTimedCheck = cycle.some((cid) => {
                                const cs = stepMap.get(cid);
                                return cs?.type === 'check_segment' && cs.wait_for != null;
                            });
                            if (!hasTimedCheck) {
                                addError(`Illegal cycle: ${cycle.join(' → ')} → ${t}. Cycles must pass through a timed check_segment.`);
                            }
                        }
                        else if (color.get(t) === WHITE) {
                            dfs(t, path);
                        }
                    }
                }
                path.pop();
                color.set(id, BLACK);
            };
            dfs(entryStepId, []);
        }
        return { errors, byStepId };
    }, [steps, entryStepId, sendWindowStart, sendWindowEnd]);
};
export default useDripGraphValidation;
